import { Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { RemindersApiService } from '@shared/services/reminders-api.service';
import { ToastrService } from 'ngx-toastr';
import { catchError, EMPTY, take, tap } from 'rxjs';
import { ChangeAuthorisation } from '@models/documents/change-authorisation';
import { DocumentBase } from '@models/documents/document-base';
import { DocumentSigning } from '@models/documents/document-signing';
import { LodgementDeadline } from "@models/documents/lodgement-deadline";
import { SigningStatus } from '@models/annual-statements/annual-statement-enums';
import { ApproverStatus } from '@models/documents/approver-status.enum';
import { ReminderStatus, ReminderType } from '@models/reminders/reminder.enum';
import { ReminderRecord } from '@models/reminders/reminder-record';
import { ModalFormsService } from '../../../../forms/services/modal-forms.service';
import { ConfirmModalComponent } from '@shared/components/commons/confirm/confirm-modal.component';
import {
  AdHocPaymentReminderComponent
} from '@shared/components/entity-profiles/ad-hoc-reminders/ad-hoc-payment-reminder/ad-hoc-payment-reminder.component';
import {
  AdHocSigningReminderComponent
} from '@shared/components/entity-profiles/ad-hoc-reminders/ad-hoc-signing-reminder/ad-hoc-signing-reminder.component';
import { ReminderComponent } from "@shared/components/documents-related/reminders-sidebar/components/reminder/reminder.component";
import { ConvertToAUDateTimeUTC, ConvertToDateTimeAu } from '@shared/utils/datetime.helper';
import { formatDate } from '@angular/common';

@Component({
  selector: 'app-reminders-sidebar',
  standalone: true,
  imports: [ReminderComponent],
  templateUrl: 'reminders-sidebar.component.html',
})
export class RemindersSidebarComponent implements OnInit {
  private modalFormsService = inject(ModalFormsService);
  private modalService = inject(NgbModal);
  private remindersService = inject(RemindersApiService);
  private toastr = inject(ToastrService);
  private destroyRef = inject(DestroyRef);

  @Input() isAnnualReminder = true;
  @Input() reminders: ReminderRecord[] | undefined;
  @Input() documentBase!: DocumentBase | null;
  @Input() paymentDeadline: string | null | undefined;
  @Input() showPaymentReminder = true;
  @Input() lodgement: LodgementDeadline | undefined;

  signingReminderList: ReminderRecord[] = [];
  paymentReminderList: ReminderRecord[] = [];
  changeAuthorisation: ChangeAuthorisation | undefined;
  ReminderType = ReminderType;
  documentSigning: DocumentSigning | undefined | null;
  documentId!: string | undefined;

  ngOnInit(): void {
    this.documentId = this.documentBase?.documentId;
    this.documentSigning = this.documentBase?.documentSigning;
    this.changeAuthorisation = this.documentBase?.changeAuthorisation;
    this.splitReminders();
  }

  splitReminders(): void {
    this.signingReminderList = this.getReminderByType(ReminderType.Signing);
    this.paymentReminderList = this.getReminderByType(ReminderType.Payment);
  }

  protected showSigningReminderPopup(): void {
    const modalRef = this.modalFormsService.openModal(AdHocSigningReminderComponent);
    const componentInstance = modalRef.componentInstance as AdHocSigningReminderComponent;

    componentInstance.isAnnualReminder = this.isAnnualReminder;
    componentInstance.documentBase = this.documentBase!;
    componentInstance.lastSendReminderDate = this.getLastSendReminderDate(ReminderType.Signing);
    componentInstance.annualPaymentDeadline = this.paymentDeadline ?? null;
    componentInstance.lodgement = this.lodgement ?? null;

    componentInstance.sendSigningAdHocReminder
      .pipe(take(1))
      .subscribe({
        next: (reminder) => {
        this.reminders?.push(reminder);
        this.splitReminders();
        }
      });
  }

  protected showPaymentReminderPopup(): void {
    const modalRef = this.modalFormsService.openModal(AdHocPaymentReminderComponent);
    const instance = modalRef.componentInstance as AdHocPaymentReminderComponent

    instance.documentBase = this.documentBase!;
    instance.annualPaymentDeadline = this.paymentDeadline ?? null;

    instance.sendPaymentAdHocReminder.pipe(
      tap((reminder) => {
        this.reminders?.push(reminder);
        this.splitReminders();
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();
  }

  getLastSendReminderDate(reminderType: ReminderType): Date | null {
    const reminderList = reminderType === ReminderType.Payment ? this.paymentReminderList : this.signingReminderList;
    const list = reminderList.filter((reminder) => reminder.status === ReminderStatus.Sent).sort((a, b) => {
      return new Date(b.scheduledOn).getTime() - new Date(a.scheduledOn).getTime();
    });

    if (list.length > 0) {
      return new Date(list[0].scheduledOn);
    }
    return null;
  }

  protected addReminder(reminderDate: Date, reminderType: ReminderType): void {
    const list = reminderType === ReminderType.Payment ? this.paymentReminderList : this.signingReminderList;
    const reminderDateAu = formatDate(new Date(ConvertToDateTimeAu(reminderDate)), 'yyyy-MM-dd', 'en-US');
    const reminderDateOnly = formatDate(reminderDate, 'yyyy-MM-dd', 'en-US');

    const listDateOnlyStrings = list.map(r => {
      const convertedDate = ConvertToDateTimeAu(new Date(r.scheduledOn));
      return formatDate(convertedDate, 'yyyy-MM-dd', 'en-US');
    });

    if(listDateOnlyStrings.includes(reminderDateAu)) {
      this.toastr.warning("Reminder already exists", "Error");
      return;
    }

    const body = {
      documentId: this.documentId ?? '',
      reminderType,
      scheduledOn: reminderDateOnly
    };

    this.remindersService.addReminder(body).pipe(
      tap((reminder) => {
        this.toastr.success("Reminder has been successfully added", "Success");
        this.reminders?.push(reminder);
        this.splitReminders();
      }),
      catchError((err) => {
        this.toastr.error("Reminder create error", "Error");
        console.error(err);
        return EMPTY;
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();
  }

  protected removeReminder(reminderId: string): void {
    const modalRef = this.modalService.open(ConfirmModalComponent);
    (modalRef.componentInstance as ConfirmModalComponent).title = 'Delete reminder';
    (modalRef.componentInstance as ConfirmModalComponent).message = 'Do you want to delete the reminder?' +
      ' If you proceed, it will not be sent on the scheduled date.';
    (modalRef.componentInstance as ConfirmModalComponent).confirmText = 'Delete';
    (modalRef.componentInstance as ConfirmModalComponent).confirm = () => this.remindersService.deleteReminder(reminderId).pipe(
      tap(() => {
        this.toastr.success("Reminder has been successfully deleted", "Success");
        this.reminders = this.reminders?.filter((reminder) => reminder.reminderId !== reminderId);
        this.splitReminders();
      }),
      catchError((err) => {
        this.toastr.error("Reminder delete error", "Error");
        console.error(err);
        return EMPTY;
      }),
    );

    modalRef.closed
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  protected getReminderByType(reminderType: ReminderType): ReminderRecord[] {
    if (!this.reminders?.length)
      return [];

    return this.reminders
      .filter((reminder) => reminder.type === reminderType)
      .sort((a, b) => {
        if (a.status !== b.status) {
          return a.status === ReminderStatus.Sent ? -1 : 1;
        }

        return new Date(a.scheduledOn).getTime() - new Date(b.scheduledOn).getTime();
      });
  }

  get isShowSigningReminder(): boolean {
    return (
      (!this.documentSigning?.approver || this.documentSigning?.approver?.approverStatus == ApproverStatus.Approved)
      && this.documentSigning?.signingStatus === SigningStatus.Sent
    );
  }

  get isShowPaymentReminder(): boolean {
    if(this.paymentReminderList.length > 0 ||
      (this.documentSigning && this.documentSigning.signingStatus > SigningStatus.Created))
      return true;

    return false;
  }
}
